import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";

interface ElementObject {
  [key: string]: any;
}

type CustomTableProps = {
  data: Array<ElementObject>;
  config?: Record<string, ElementObject>;
  horizontal?: boolean;
};

export default function CustomTable(props: CustomTableProps) {
  return props.horizontal ? (
    <HorizontalTable {...props} />
  ) : (
    <VerticalTable {...props} />
  );
}

function HorizontalTable({ data, config }: CustomTableProps) {
  const { t } = useTranslation();
  function Row({ title, values }: any) {
    return (
      <>
        <TableRow style={{ fontWeight: "bold" }}>
          <StyledTableCell width={30} align="left" sx={{ fontWeight: "bold" }}>
            {t(`FORM.FIELD.${title}`)}
          </StyledTableCell>
          {values.map((value: any, i: number) => (
            <StyledTableCell key={i} align="right">
              {value}
            </StyledTableCell>
          ))}
        </TableRow>
      </>
    );
  }
  const formattedData: Record<string, Array<any>> = {};
  data.forEach((element) => {
    Object.keys(element).forEach((key) => {
      if (key in formattedData) {
        formattedData[key].push([element[key]]);
      } else {
        formattedData[key] = [element[key]];
      }
    });
  });
  const headerKey = Object.keys(formattedData)[0];
  const headers = formattedData[headerKey];
  delete formattedData[headerKey];
  return (
    <Table
      sx={{
        tableLayout: "fixed",
        "& tbody tr:last-child td": {
          borderBottom: "none",
        },
      }}
      aria-label="simple table"
    >
      <TableHead>
        <Row title={headerKey} values={headers} />
      </TableHead>
      <TableBody>
        {Object.keys(formattedData).map((title: string) => (
          <Row title={title} values={formattedData[title]} />
        ))}
      </TableBody>
    </Table>
  );
}

function VerticalTable({ data, config }: CustomTableProps) {
  const headers = Object.keys(data[0]);
  const { t } = useTranslation();

  return (
    <Table
      sx={{
        tableLayout: "fixed",
        "& tbody tr:last-child td": {
          borderBottom: "none",
        },
      }}
      aria-label="simple table"
    >
      <TableHead>
        <TableRow style={{ fontWeight: "bold" }}>
          {headers.map((header, i: number) => (
            <StyledTableCell
              key={i}
              align="center"
              {...(config && header in config && config[header])}
            >
              {t(`FORM.FIELD.${header}`)}
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((element: ElementObject, i: number) => (
          <TableRow
            key={i}
            sx={{
              overflow: "clip",
            }}
          >
            {Object.keys(element).map((keyValue: string, j: number) => (
              <StyledTableCell
                key={j}
                align="center"
                {...(config && keyValue in config && config[keyValue])}
              >
                {element[keyValue]}
              </StyledTableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    padding: 0,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    padding: "5px 0",
    fontSize: 14,
  },
  [`&.${tableCellClasses.body}:first-of-type`]: {
    fontWeight: "bold",
  },
}));
